import React, { useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  LinearProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { useSnackbar } from '../components/AppSnackbar';
import { useAuth } from '../hooks/useAuth';
import ConfirmDialog from '../components/ConfirmDialog';
import type { AssetCategory, AssetStatus } from '@shared/types/asset.types';

// ============================================================
// Constants
// ============================================================

const CATEGORY_LABELS: Record<AssetCategory, string> = {
  pc: 'PC',
  server: 'サーバー',
  nas: 'NAS',
  network_device: 'ネットワーク機器',
  monitor: 'モニター',
  printer: 'プリンター',
  software_license: 'ソフトウェアライセンス',
  smartphone: 'スマートフォン',
  tablet: 'タブレット',
  other: 'その他備品',
};

const STATUS_LABELS: Record<AssetStatus, string> = {
  in_use: '使用中',
  in_storage: '保管中',
  under_repair: '修理中',
  disposed: '廃棄済み',
  lost: '紛失',
};

const STATUS_COLORS: Record<AssetStatus, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  in_use: 'success',
  in_storage: 'info',
  under_repair: 'warning',
  disposed: 'default',
  lost: 'error',
};

// ============================================================
// Types
// ============================================================

interface AssetDetailData {
  id: string;
  assetNumber: string;
  category: AssetCategory;
  manufacturer: string | null;
  modelNumber: string | null;
  serialNumber: string | null;
  purchaseDate: string | null;
  maintenanceDeadline: string | null;
  renewalDeadline: string | null;
  location: string | null;
  department: string | null;
  userName: string | null;
  managerId: string | null;
  managerName: string | null;
  status: AssetStatus;
  notificationDaysOverride: number | null;
  purchasedLicenses: number | null;
  usedLicenses: number | null;
  notes: string | null;
  createdAt: string;
  createdBy: string | null;
  createdByName: string | null;
  updatedAt: string;
  updatedBy: string | null;
  updatedByName: string | null;
}

interface AttachmentData {
  id: string;
  assetId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  storagePath: string;
  uploadedBy: string;
  createdAt: string;
  uploader: { id: string; name: string };
}

// ============================================================
// Helper
// ============================================================

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ============================================================
// Component
// ============================================================

const AssetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { user } = useAuth();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attachmentDeleteId, setAttachmentDeleteId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  // Fetch asset data
  const {
    data: asset,
    isLoading,
    isError,
    error: fetchError,
  } = useQuery<AssetDetailData>({
    queryKey: ['asset', id],
    queryFn: async () => {
      const response = await api.get(`/assets/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/assets/${id}`);
    },
    onSuccess: () => {
      showSuccess('資産を削除しました');
      navigate('/assets');
    },
    onError: () => {
      showError('資産の削除に失敗しました');
    },
  });

  // Fetch attachments
  const {
    data: attachments = [],
    isLoading: attachmentsLoading,
  } = useQuery<AttachmentData[]>({
    queryKey: ['attachments', id],
    queryFn: async () => {
      const response = await api.get(`/assets/${id}/attachments`);
      return response.data;
    },
    enabled: !!id,
  });

  // Upload attachment mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/assets/${id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      showSuccess('ファイルをアップロードしました');
      queryClient.invalidateQueries({ queryKey: ['attachments', id] });
      setUploading(false);
    },
    onError: () => {
      showError('ファイルのアップロードに失敗しました');
      setUploading(false);
    },
  });

  // Delete attachment mutation
  const deleteAttachmentMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      await api.delete(`/assets/attachments/${attachmentId}`);
    },
    onSuccess: () => {
      showSuccess('添付ファイルを削除しました');
      queryClient.invalidateQueries({ queryKey: ['attachments', id] });
    },
    onError: () => {
      showError('添付ファイルの削除に失敗しました');
    },
  });

  // ============================================================
  // Handlers
  // ============================================================

  const handleEdit = () => {
    navigate(`/assets/${id}/edit`);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    deleteMutation.mutate();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) processFile(file);
    // Reset input
    event.target.value = '';
  };

  const processFile = (file: File) => {
    // 100MB limit check
    if (file.size > 100 * 1024 * 1024) {
      showError('ファイルサイズは100MB以下にしてください');
      return;
    }
    setUploading(true);
    uploadMutation.mutate(file);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDownload = async (attachmentId: string, fileName: string) => {
    try {
      const response = await api.get(`/assets/attachments/${attachmentId}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showError('ファイルのダウンロードに失敗しました');
    }
  };

  const handleAttachmentDeleteClick = (attachmentId: string) => {
    setAttachmentDeleteId(attachmentId);
  };

  const handleAttachmentDeleteConfirm = () => {
    if (attachmentDeleteId) {
      deleteAttachmentMutation.mutate(attachmentDeleteId);
    }
    setAttachmentDeleteId(null);
  };

  const handleAttachmentDeleteCancel = () => {
    setAttachmentDeleteId(null);
  };

  // ============================================================
  // Loading / Error states
  // ============================================================

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !asset) {
    const axiosErr = fetchError as { response?: { status?: number } } | null;
    const is404 = axiosErr?.response?.status === 404;
    return (
      <Box>
        <Typography variant="h5" color="error">
          {is404 ? '資産が見つかりません' : '資産データの読み込みに失敗しました'}
        </Typography>
        <Button
          sx={{ mt: 2 }}
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to="/assets"
        >
          資産台帳一覧に戻る
        </Button>
      </Box>
    );
  }

  // ============================================================
  // Render
  // ============================================================

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px - 48px)', overflow: 'hidden' }}>
      {/* Fixed header portion */}
      <Box sx={{ flexShrink: 0, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            資産詳細
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isAdmin && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  編集
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteClick}
                >
                  削除
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Back link */}
        <Button
          component={RouterLink}
          to="/assets"
          startIcon={<ArrowBackIcon />}
          size="small"
        >
          資産台帳一覧に戻る
        </Button>
      </Box>

      {/* Scrollable content portion */}
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>

      {/* Asset details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          基本情報
        </Typography>
        <Grid container spacing={2}>
          <DetailItem label="資産番号" value={asset.assetNumber} />
          <DetailItem
            label="資産区分"
            value={CATEGORY_LABELS[asset.category] || asset.category}
          />
          <DetailItem label="メーカー" value={asset.manufacturer} />
          <DetailItem label="型番" value={asset.modelNumber} />
          <DetailItem label="シリアル番号" value={asset.serialNumber} />
          <DetailItem label="購入日" value={formatDate(asset.purchaseDate)} />
          <DetailItem label="保守期限" value={formatDate(asset.maintenanceDeadline)} />
          <DetailItem label="更新期限" value={formatDate(asset.renewalDeadline)} />
          <DetailItem label="設置場所" value={asset.location} />
          <DetailItem label="使用部署" value={asset.department} />
          <DetailItem label="使用者" value={asset.userName} />
          <DetailItem label="管理者" value={asset.managerName} />
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              状態
            </Typography>
            <Chip
              label={STATUS_LABELS[asset.status] || asset.status}
              color={STATUS_COLORS[asset.status] || 'default'}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <DetailItem
            label="通知期限日数"
            value={
              asset.notificationDaysOverride != null
                ? `${asset.notificationDaysOverride}日`
                : 'マスタ設定のデフォルト値'
            }
          />
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              備考
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
              {asset.notes || '-'}
            </Typography>
          </Grid>
        </Grid>

        {/* Software license section */}
        {asset.category === 'software_license' && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              ライセンス情報
            </Typography>
            <Grid container spacing={2}>
              <DetailItem
                label="購入ライセンス数"
                value={asset.purchasedLicenses != null ? String(asset.purchasedLicenses) : null}
              />
              <DetailItem
                label="使用ライセンス数"
                value={asset.usedLicenses != null ? String(asset.usedLicenses) : null}
              />
              <DetailItem
                label="ライセンス残数"
                value={
                  asset.purchasedLicenses != null && asset.usedLicenses != null
                    ? String(asset.purchasedLicenses - asset.usedLicenses)
                    : null
                }
              />
            </Grid>
          </>
        )}
      </Paper>

      {/* Audit information */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          監査情報
        </Typography>
        <Grid container spacing={2}>
          <DetailItem label="作成日時" value={formatDateTime(asset.createdAt)} />
          <DetailItem label="作成者" value={asset.createdByName} />
          <DetailItem label="更新日時" value={formatDateTime(asset.updatedAt)} />
          <DetailItem label="更新者" value={asset.updatedByName} />
        </Grid>
      </Paper>

      {/* Attachments section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <AttachFileIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            添付ファイル
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={uploading}
            size="small"
          >
            {uploading ? 'アップロード中...' : 'ファイルを追加'}
            <input
              type="file"
              hidden
              onChange={handleFileUpload}
            />
          </Button>
        </Box>

        {/* ドラッグ&ドロップエリア */}
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            p: 2,
            mb: 2,
            border: '2px dashed',
            borderColor: isDragOver ? 'primary.main' : 'grey.300',
            borderRadius: 1,
            textAlign: 'center',
            backgroundColor: isDragOver ? 'action.hover' : 'transparent',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
          }}
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.onchange = (e) => {
              const file = (e.target as HTMLInputElement).files?.[0];
              if (file) processFile(file);
            };
            input.click();
          }}
        >
          <UploadFileIcon sx={{ fontSize: 32, color: 'grey.500' }} />
          <Typography variant="body2" color="text.secondary">
            ファイルをドラッグ&ドロップ、またはクリックして選択
          </Typography>
          <Typography variant="caption" color="text.secondary">
            最大100MB
          </Typography>
        </Box>

        {uploading && <LinearProgress sx={{ mb: 2 }} />}

        {attachmentsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : attachments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            添付ファイルはありません
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ファイル名</TableCell>
                  <TableCell>サイズ</TableCell>
                  <TableCell>アップロード日時</TableCell>
                  <TableCell>アップロード者</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attachments.map((attachment) => (
                  <TableRow key={attachment.id}>
                    <TableCell>{attachment.fileName}</TableCell>
                    <TableCell>{formatFileSize(attachment.fileSize)}</TableCell>
                    <TableCell>{formatDateTime(attachment.createdAt)}</TableCell>
                    <TableCell>{attachment.uploader?.name || '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(attachment.id, attachment.fileName)}
                        title="ダウンロード"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      {isAdmin && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleAttachmentDeleteClick(attachment.id)}
                          title="削除"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      </Box>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="資産の削除"
        message={`資産「${asset.assetNumber}」を削除してもよろしいですか？この操作は取り消せません。`}
        confirmLabel="削除"
        cancelLabel="キャンセル"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        severity="error"
      />

      {/* Attachment delete confirmation dialog */}
      <ConfirmDialog
        open={!!attachmentDeleteId}
        title="添付ファイルの削除"
        message="この添付ファイルを削除してもよろしいですか？この操作は取り消せません。"
        confirmLabel="削除"
        cancelLabel="キャンセル"
        onConfirm={handleAttachmentDeleteConfirm}
        onCancel={handleAttachmentDeleteCancel}
        severity="error"
      />
    </Box>
  );
};

// ============================================================
// Sub-components
// ============================================================

interface DetailItemProps {
  label: string;
  value: string | null | undefined;
}

const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ mt: 0.5 }}>
      {value || '-'}
    </Typography>
  </Grid>
);

export default AssetDetailPage;
